import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Heading } from "./NKDaily";
import { CountryFlag } from "@/lib/identity";
import { ArrowSquareOut, Compass } from "@phosphor-icons/react";

/**
 * Expansion Watchlist — African countries that aren't yet playoff-eligible
 * but are approaching the min_active_threshold. Sorted by closest to ready.
 */
export default function ExpansionWatchlist() {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/admin/countries/watchlist")
      .then((r) => setRows(r.data?.rows || []))
      .catch(() => setRows([]));
  }, []);

  const tiers = useMemo(() => {
    const ready = rows.filter((r) => (r.gap_to_threshold ?? 999) === 0);
    const close = rows.filter((r) => (r.gap_to_threshold ?? 999) > 0 && (r.gap_to_threshold ?? 999) <= 5);
    const growing = rows.filter((r) => (r.gap_to_threshold ?? 999) > 5);
    return { ready, close, growing };
  }, [rows]);

  return (
    <div className="space-y-6">
      <Heading
        title="Expansion Watchlist"
        subtitle="African countries closest to playoff readiness. Pass the activity threshold for 2-3 consecutive seasons + local payout route, then flip 'Playoff' ON in the Countries panel."
      />

      <Tier title="Ready to Promote" subtitle="At or past their min_active_threshold this season."
            rows={tiers.ready} accent="gold" testid="watchlist-tier-ready" />

      <Tier title="Closing the Gap" subtitle="Within 5 players of the threshold."
            rows={tiers.close} accent="accent" testid="watchlist-tier-close" />

      <Tier title="Early Growth" subtitle="Active but still building a player base."
            rows={tiers.growing} accent="mute" testid="watchlist-tier-growing" />

      {rows.length === 0 && (
        <div className="nk-card p-10 text-center text-nk-mute text-sm">
          <Compass size={28} weight="duotone" className="text-nk-dim mx-auto mb-2" />
          No African countries are tracked here yet. The watchlist activates once players sign up from non-playoff African countries.
        </div>
      )}
    </div>
  );
}

function Tier({ title, subtitle, rows, accent, testid }) {
  const accentClass = accent === "gold" ? "border-nk-gold/40 text-nk-gold" : accent === "accent" ? "border-nk-accent/40 text-nk-accent" : "border-nk-line text-nk-mute";
  return (
    <section data-testid={testid}>
      <div className="flex items-baseline gap-3 mb-3">
        <p className={`eyebrow ${accent === "gold" ? "text-nk-gold" : accent === "accent" ? "text-nk-accent" : ""}`}>{title}</p>
        <span className="text-xs text-nk-dim font-mono">{rows.length}</span>
        <span className="text-xs text-nk-dim ml-auto sm:hidden md:inline">{subtitle}</span>
      </div>
      <p className="text-xs text-nk-dim mb-3 md:hidden">{subtitle}</p>
      {rows.length === 0 ? (
        <div className="nk-card p-4 text-xs text-nk-dim">No countries in this tier.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {rows.map((c) => {
            const ratio = c.min_active_threshold ? Math.min(1, (c.ranked_players || 0) / c.min_active_threshold) : 0;
            return (
              <Link to={`/country/${c.country_code}`} key={c.country_code}
                    data-testid={`watchlist-row-${c.country_code.toLowerCase()}`}
                    className={`nk-card p-4 border-l-2 ${accentClass} hover:border-nk-gold transition-colors block`}>
                <div className="flex items-center gap-3 mb-2.5">
                  <CountryFlag country={c.country_code} size={18} />
                  <p className="font-medium flex-1 truncate">{c.country_name}</p>
                  <span className="text-[10px] text-nk-dim">{c.region}</span>
                  <ArrowSquareOut size={10} className="text-nk-dim" />
                </div>
                <div className="flex items-center gap-3 text-[11px]">
                  <span className="text-nk-mute">Players: <span className="font-mono text-nk-text">{c.total_players}</span></span>
                  <span className="text-nk-mute">Ranked: <span className="font-mono text-nk-accent">{c.ranked_players}</span></span>
                  <span className="text-nk-mute ml-auto">Threshold: <span className="font-mono">{c.min_active_threshold || "—"}</span></span>
                </div>
                <div className="mt-2 h-1 bg-nk-line/40 rounded-sm overflow-hidden">
                  <div className={`h-full ${accent === "gold" ? "bg-nk-gold" : "bg-nk-accent"}`} style={{ width: `${ratio * 100}%` }} />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}
